import React from 'react'
import { Badge, Dropdown } from 'react-bootstrap';
import { BookingsResponseV4, BookingV4 } from '../../../services/create-booking-v4/schema/GetAllBookingResponseV4';
import clsx from 'clsx';
import { IconArrowsSort, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import { formatPrice } from '../../../shared/utils/formatPrice';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';

interface ReservationTableProps {
    bookings: BookingsResponseV4 | undefined,
    onUpdate: (id: string) => void,
    onAssignRoom: (reservation: BookingV4) => void,
    setSortColumn: (column: string) => void,
    setSortDirection: (direction: "asc" | "desc") => void,
    sortColumn: string | null | undefined,
    sortDirection: "asc" | "desc" | null | undefined,
    isFetching: boolean,
    onDelete: (id: string) => void,
    onViewDetail: (id: string) => void
}

const TableHeading = [
    { value: "invoice_number", label: "Reservation", disabled: false },
    { value: "ota_name", label: "OTA Name", disabled: false },
    { value: "property.name", label: "Property", disabled: true },
    { value: "agent_name", label: "Agent", disabled: false },
    { value: "invoice_date", label: "Created", disabled: false },
    { value: "checkin_date", label: "Arrival", disabled: false },
    { value: "checkout_date", label: "Departure", disabled: false },
    { value: "customer_name", label: "Guest Name", disabled: false },
    { value: "room_type.name", label: "Room", disabled: true },
    { value: "rate_plan.name", label: "Rateplan", disabled: true },
    { value: "property.preferred_currency", label: "Due Amount", disabled: true },
    { value: "property.preferred_currency", label: "Deposit Amount", disabled: true },
    { value: "room.room_number", label: "Assign Room", disabled: true },
    { value: "status", label: "Status", disabled: false },
]

const ReservationTable: React.FC<ReservationTableProps> = ({
    bookings,
    onUpdate,
    onAssignRoom,
    setSortColumn,
    setSortDirection,
    sortColumn,
    sortDirection,
    isFetching,
    onDelete,
    onViewDetail
}) => {
    const getStatusStyles = (status: string | null | undefined) => {
        switch (status?.toLowerCase()) {
            case 'confirmed':
                return { bg: '#D1E7DD', text: '#0F5132', icon: '#198754' }; // Soft Green
            case 'cancelled':
                return { bg: '#F8D7DA', text: '#842029', icon: '#DC3545' }; // Soft Red
            case 'pending':
            case 'unconfirm':
                return { bg: '#FFF3CD', text: '#664D03', icon: '#FFC107' }; // Soft Yellow
            case 'modified':
                return { bg: '#CFF4FC', text: '#055160', icon: '#0DCAF0' }; // Soft Blue
            default:
                return { bg: '#E2E3E5', text: '#41464B', icon: '#6C757D' }; // Soft Gray
        }
    };

    const getStatusIcon = (status: string | null | undefined, color?: string) => {
        const iconStyle = color ? { color } : {};
        switch (status?.toLowerCase()) {
            case 'confirmed':
                // Checkmark
                return <svg style={iconStyle} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="me-2"><polyline points="20 6 9 17 4 12"></polyline></svg>;
            case 'cancelled':
                // X / Ban
                return <svg style={iconStyle} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="me-2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>;
            case 'pending':
            case 'unconfirm':
                // Clock / Hourglass
                return <svg style={iconStyle} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="me-2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>;
            case 'modified':
                // Edit / Refresh
                return <svg style={iconStyle} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="me-2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
            default:
                return null;
        }
    };
    function formatDateToDayMonthYear(date: string): string {
        const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
        return new Date(date).toLocaleDateString(undefined, options);
    }

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc"); // Default to ascending on new column selection
        }
    };

    const getSortIcon = (column: string) => {
        if (sortColumn === column) {
            return sortDirection === "asc" ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />;
        }
        return <IconArrowsSort size={16} />;
    };

    console.log('bookings', bookings);

    const bookingCondition = (booking: BookingV4) => {
        if (booking && (booking.rate_plan_id == null || booking.room_type_id == null)) {
            return { className: "table-warning", text: "failed" };
        }

        if (booking && (booking.rate_plan_id != null && booking.room_type_id != null) && (booking.room?.room_number == null)) {
            return { className: "table-danger", text: "Unassigned" };
        }

        return { className: "", text: booking.room?.room_number };
    };


    return (
        <div className="table-wrapper position-relative table-responsive">
            <table className="table table-hover table-vcenter">
                <thead>
                    <tr>
                        {TableHeading.map((column, index) => (
                            <th key={index} onClick={() => {
                                if (!column.disabled) {
                                    handleSort(column.value)
                                }
                            }} style={{ cursor: "pointer" }}>
                                <div className='d-flex justify-content-between'>
                                    <span className='me-1'>{column.label}</span>
                                    {column.disabled ? null : getSortIcon(column.value)}
                                </div>
                            </th>
                        ))}
                        <th className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {isFetching ? (
                        // Skeleton Loader (Menampilkan placeholder saat data dimuat)
                        [...Array(5)].map((_, index) => (
                            <tr key={index}>
                                {Array(14).fill(null).map((_, colIndex) => (
                                    <td key={colIndex}>
                                        <div className="placeholder-glow">
                                            <span className="placeholder col-12"></span>
                                        </div>
                                    </td>
                                ))}
                                <td className="text-end">
                                    <div className="placeholder-glow">
                                        <span className="placeholder col-6"></span>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (!bookings || !bookings.data || bookings.data.length === 0) ? (
                        <tr>
                            <td colSpan={15} className="text-center">No data found</td>
                        </tr>
                    ) : (
                        // Data tersedia, tampilkan isi tabel
                        bookings.data.map((reservation, index) => (
                            <React.Fragment key={index}>
                                <tr className={clsx(bookingCondition(reservation).className)}>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            {getStatusIcon(reservation.status, getStatusStyles(reservation.status).icon)}
                                            <span
                                                className="text-primary fw-bold"
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                onClick={() => onViewDetail(reservation.id)}
                                            >
                                                {reservation.invoice_number}
                                            </span>
                                        </div>
                                    </td>
                                    <td>{reservation.ota_name}</td>
                                    <td>{reservation.property?.name}</td>
                                    <td>{reservation.agent_name ?? '-'}</td>
                                    <td>{formatDateToDayMonthYear(reservation.invoice_date)}</td>
                                    <td>{formatDateToDayMonthYear(reservation.checkin_date)}</td>
                                    <td>{formatDateToDayMonthYear(reservation.checkout_date)}</td>
                                    <td>{`${reservation.customer_name} ${reservation.customer_surname}`}</td>
                                    <td>{reservation.room_type?.name}</td>
                                    <td>{reservation.rate_plan?.name}</td>
                                    <td>{formatPrice(Number(reservation.due_payment) || 0)}</td>
                                    <td>{formatPrice(Number(reservation.down_payment) || 0)}</td>
                                    <td>{bookingCondition(reservation).text}</td>
                                    <td>
                                        <Badge
                                            bg=""
                                            style={{
                                                backgroundColor: getStatusStyles(reservation.status).bg,
                                                color: getStatusStyles(reservation.status).text,
                                            }}
                                            className="d-inline-flex align-items-center px-2 py-1 fw-bold text-uppercase"
                                        >
                                            {reservation.status ? reservation.status : 'No Status'}
                                        </Badge>
                                    </td>
                                    <td className="text-end">
                                        <Dropdown>
                                            <Dropdown.Toggle variant="default" id="dropdownMenuButton1">
                                                Actions
                                            </Dropdown.Toggle>
                                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                                                <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                                    <Dropdown.Item href="#" onClick={() => onViewDetail(reservation.id)}>
                                                        View Details
                                                    </Dropdown.Item>
                                                    <ReadOnlyGuard mode="hide">
                                                        <Dropdown.Item href="#" onClick={() => onUpdate(reservation.id)}>Edit</Dropdown.Item>
                                                    </ReadOnlyGuard>
                                                    {bookingCondition(reservation).className === "table-warning" && (
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item href='#' onClick={() => onAssignRoom(reservation)}>Assign Room</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    )}
                                                    <ReadOnlyGuard mode="hide">
                                                        <Dropdown.Item href="#" className="text-danger" onClick={() => onDelete(reservation.id)}>
                                                            Delete
                                                        </Dropdown.Item>
                                                    </ReadOnlyGuard>
                                                </Dropdown.Menu>
                                            </RequireAuth>
                                        </Dropdown>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    )
}

export default ReservationTable